import colorPallete from "@DSG/MobiusOverrides/styles/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { createRef, FC, useRef, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            max-width: 670px;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;

            &.active-brand {
                * {
                    pointer-events: none;
                }
                p[class*="TypographyStyle"]:not(.navigation-empty) {
                    background: ${colorPallete.grey800};
                    color: ${colorPallete.white};
                }
            }
        `,
    },
    navigationLink: {
        css: css`
            display: block;
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
        css: css`
            pointer-events: none;
        `
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => props.alphabet);

    const currentButtonRef = useRef<React.RefObject<HTMLSpanElement>[]>([]);
    currentButtonRef.current = alphabet.map((element, i) => currentButtonRef.current[i] ?? createRef());

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };
    const createHandleClickOfLetter = (letter: string) => () => {
        const letterElement = document.querySelector(`[data-test-key="${letter}"]`);
        if (letterElement) {
            letterElement.scrollIntoView({ behavior: 'smooth' });
        }
        handleClickOfLetter(letter);
        return "";
    };

    const runFunction = (index: number) => {
        
        currentButtonRef.current.forEach((ref, i) => {
          if (ref.current) {
            if (i === index) {
              ref.current.classList.add('active-brand');
            } else {
              ref.current.classList.remove('active-brand');
            }
          }
        });
      };

    if (alphabet.length <= 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
            {alphabet.map((alphabetItem, i) => (
                <StyledSpan
                    {...styles.navigationItem}
                    key={alphabetItem.letter}
                    data-test-selector="alphabetNavigationItemLetter"
                    ref={currentButtonRef.current[i]}
                    className={`${alphabetItem.letter === '#' ? 'hashAlphabet' : '' }`}
                    onClick={() => runFunction(i)}
                >
                    {alphabetItem.linkable ? (
                        <Link {...styles.navigationLink} onClick={createHandleClickOfLetter(alphabetItem.letter)} data-test-selector={`brand-letter-${alphabetItem.letter}`}>
                            {alphabetItem.letter}
                        </Link>
                    ) : (
                        <Typography className="navigation-empty" {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                    )}
                </StyledSpan>
            ))}
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
